import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Modal, Image, Badge, Form, Spinner, Alert } from 'react-bootstrap';
import { adminInstance } from '../services/axiosInstance';
import LoaderOverlay from '../Components/shared/Loader';

const ExpenseApprovalPage = ({ ReadOnly = true }) => {
    //const { id } = useParams();
    const hierarchy = ReadOnly ? "Employee" : sessionStorage.getItem('role');
    const { requestId } = useParams();
    const navigate = useNavigate();
    const [request, setRequest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [approvalComments, setApprovalComments] = useState('');
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');
    const [processing, setProcessing] = useState(false);

    // Fetch request details
    useEffect(() => {
        const fetchRequest = async () => {
            try {
                const response = await adminInstance.get(`/expenserequest/GetById/${requestId}`);
                setRequest(response.data);
            } catch (err) {
                setError('Failed to load expense request');
                console.error('Error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchRequest();
    }, [requestId]);

    // Handle approval actions
    const handleApproveOrReject = async (status) => {
        try {
            if (status === "Rejected" && !approvalComments) {
                alert('Please provide comments for rejection');
                return;
            }

            setLoading(true);
            setProcessing(true);
            var obj = {
                IS_ADMIN: hierarchy === "Admin" ? true : false,
                REQ_ID: requestId,
                STATUS: status,
                COMMENT: approvalComments,
            };

            await adminInstance.put(`/expenserequest/UpdateStatus/${hierarchy}`, obj);
            navigate('/ExpenseApprovalList', { state: { message: 'Request approved successfully' } });
        } catch (err) {
            setError('Approval failed. Please try again.');
            setProcessing(false);
        } finally {
            setLoading(false);
        }
    };

    // Handle Cancel actions
    const handleCancel = async () => {
        try {            
            setLoading(true);
            setProcessing(true);
            await adminInstance.post(`/expenserequest/cancel/${requestId}`, null);
            navigate('/MyExpenseRequest', { state: { message: 'Request cancelled successfully' } });
        } catch (err) {
            setError('Cancel failed. Please try again.');
            setProcessing(false);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center mt-5">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mt-5">
                <Alert variant="danger">{error}</Alert>
                <Button variant="secondary" onClick={() => navigate(-1)}>
                    Go Back
                </Button>
            </div>
        );
    }

    if (!request) {
        return (
            <div className="container mt-5">
                <Alert variant="warning">Expense request not found</Alert>
                <Button variant="secondary" onClick={() => navigate(-1)}>
                    Go Back
                </Button>
            </div>
        );
    }

    // Helper functions
    const getStatusBadge = () => {
        switch (request.REQ_STATUS) {
            case 'Open': return 'warning';
            case 'Approved': return 'success';
            case 'Completed': return 'success';
            case 'Rejected': return 'danger';
            default: return 'secondary';
        }
    };

    // The trip carries the real stage once the admin completes and the auditor closes it,
    // so it gets its own badge next to the request status.
    const getTripStatusBadge = () => {
        switch (request.TRIP_STATUS) {
            case 'Open':
            case 'Submitted': return 'warning';
            case 'Approved':
            case 'Completed':
            case 'Closed': return 'success';
            case 'Rejected': return 'danger';
            default: return 'secondary';
        }
    };

    const formatDate = (dateString) => {
        return dateString ? new Date(dateString).toLocaleDateString() : 'N/A';
    };

    const travelTotal = request.ITEMS?.reduce((sum, item) => sum + (item.AMOUNT || 0), 0) || 0;

    const getValidationTags = (req) => {
        debugger;
        const tags = [];
        const designationLimits = {
            Manager: {
                FOOD: 1000,
                STATIONARY: 200,
                COURIER: 200,
                TRAVEL: 2000,
                STAY: 5000,
                OTHER_EXPENSE: 300
            },
            Executive: {
                FOOD: 1000,
                STATIONARY: 200,
                COURIER: 200,
                TRAVEL: 2000,
                STAY: 5000,
                OTHER_EXPENSE: 300
            },
            Default: {
                FOOD: 1000,
                STATIONARY: 200,
                COURIER: 200,
                TRAVEL: 2000,
                STAY: 5000,
                OTHER_EXPENSE: 300
            }
        };
        const limits = designationLimits["Default"] || {};

        // Rule 1: Category-specific limits
        var cats = ["FOOD", "STATIONARY", "COURIER", "TRAVEL", "STAY", "OTHER_EXPENSE"];
        cats.forEach(cat => {
            const categoryLimit = limits[cat];
            if (categoryLimit !== undefined) {
                tags.push({
                    label:
                        req[cat] <= categoryLimit
                            ? `${cat} ✓`
                            : `${cat} Over ₹${categoryLimit}`,
                    pass: req[cat] <= categoryLimit,
                });
            }
        });

        // Rule 1: Food limit ₹500
        //if (req.category === "Food") {
        //    tags.push({
        //        label: req.amount <= 500 ? "Food ✓" : "Food Over ₹500",
        //        pass: req.amount <= 500,
        //    });
        //}

        //// Rule 2: Travel limit ₹1000
        //if (req.category === "Travel") {
        //    tags.push({
        //        label: req.amount <= 1000 ? "Travel ✓" : "Travel Over ₹1000",
        //        pass: req.amount <= 1000,
        //    });
        //}

        //// Rule 3: Stay limit ₹3000
        //if (req.category === "Stay") {
        //    tags.push({
        //        label: req.amount <= 3000 ? "Stay ✓" : "Stay Over ₹3000",
        //        pass: req.amount <= 3000,
        //    });
        //}

        // Rule 4: Bill must be uploaded
        tags.push({
            label: req.REQ_IMAGES.length > 0 ? "BILL ATTACHED" : "MISSING BILL",
            pass: !!req.REQ_IMAGES.length > 0,
        });

        // Rule 5: Weekend expense (Saturday or Sunday)
        const day = new Date(req.expenseDate).getDay();
        const isWeekend = day === 0 || day === 6;
        tags.push({
            label: isWeekend ? "WEEKEND EXPENSE" : "WEEKDAY ✓",
            pass: !isWeekend,
        });

        return tags;
    };

    return (
        <div className="card aon-card">
            <LoaderOverlay loading={loading} message="Loading" height="500px" />
            <div className="card-header aon-card-header">
                <h4>
                    Reimbursement Approval
                </h4>
            </div>
            <div className="card-body aon-card-body">
                <div className="container">
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2>#{request.ID} | {request.EMP_NAME}</h2>
                    <div className="d-flex align-items-center">
                        <Badge bg={getStatusBadge()} className="me-2">
                            {request.REQ_STATUS}
                        </Badge>
                        {request.TRIP_STATUS && (
                            <Badge bg={getTripStatusBadge()} className="me-2">
                                Trip: {request.TRIP_STATUS}
                            </Badge>
                        )}
                        <span className="text-muted">
                            Submitted on: {formatDate(request.REQ_DATE)}
                        </span>
                    </div>
                    </div>
                <div>
                {((request.REQ_STATUS === 'Open' || request.REQ_STATUS === 'Draft') && ReadOnly === true) && (
                    <Button variant="danger" onClick={() => handleCancel()}>
                        Cancel Request
                    </Button>)}
                    <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                        Back to List
                    </Button></div>
            </div>

            {/* Employee and Request Info */}
            <Row className="mb-4">
                <Col md={6}>
                    <Card className="h-100">
                        <Card.Body>
                            <Card.Title>Employee Details</Card.Title>
                            <div className="employee-details">
                                <p><strong>Name:</strong> {request.EMP_NAME || 'N/A'}</p>
                                <p><strong>Employee Code:</strong> {request.EMP_CODE || 'N/A'}</p>
                                <p><strong>Designation:</strong> {request.EMP_DESIGNATION || 'N/A'}</p>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="h-100">
                        <Card.Body>
                            <Card.Title>Request Summary</Card.Title>
                            <div className="request-summary">
                                <p><strong>Travel Date:</strong> {formatDate(request.TRAVEL_DATE)}</p>
                                <p><strong>Travel Type:</strong> {request.TRAVEL_TYPE || 'N/A'}</p>
                                <p><strong>Customer Visited:</strong> {request.CUSTOMER || 'N/A'}</p>
                                <p><strong>Service Number:</strong> {request.SERVICE_NO || 'N/A'}</p>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Expense Breakdown */}
            <Card className="mb-4">
                <Card.Body>
                    <Card.Title className="mb-4">Expense Details</Card.Title>

                    {/* Travel Expenses - Only show if items exist */}
                    {request.ITEMS?.length > 0 ? (
                        <div className="mb-4">
                            <h5>Travel Expenses</h5>
                            <div className="table-responsive">
                                <table className="table table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th>From</th>
                                            <th>To</th>
                                            <th>Distance (km)</th>
                                            <th>Mode</th>
                                            <th>Vehicle</th>
                                            <th>Amount (₹)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {request.ITEMS.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.TRV_FROM || 'N/A'}</td>
                                                <td>{item.TRV_TO || 'N/A'}</td>
                                                <td>{item.DISTANCE?.toFixed(2) || 'N/A'}</td>
                                                <td>{item.MODE === 'O' ? 'Own Vehicle' : 'Public Transport'}</td>
                                                <td>{item.VEHICLE || 'N/A'}</td>
                                                <td>₹{item.AMOUNT?.toFixed(2) || '0.00'}</td>
                                            </tr>
                                        ))}
                                        <tr className="table-active">
                                            <td colSpan="5" className="text-end"><strong>Travel Total:</strong></td>
                                            <td><strong>₹{travelTotal.toFixed(2)}</strong></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <Alert variant="info" className="mb-4">No travel expenses recorded</Alert>
                    )}

                    {/* Other Expenses */}
                    <Row>
                        <Col md={4}>
                            <Card className="mb-3">
                                <Card.Body>
                                    <Card.Title>Food</Card.Title>
                                    <Card.Text className="fs-4">
                                        ₹{request.FOOD?.toFixed(2) || '0.00'}
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                        {request.TRAVEL_TYPE === 'Outstation' && (
                            <Col md={4}>
                                <Card className="mb-3">
                                    <Card.Body>
                                        <Card.Title>Stay</Card.Title>
                                        <Card.Text className="fs-4">
                                            ₹{request.STAY?.toFixed(2) || '0.00'}
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                        <Col md={4}>
                            <Card className="mb-3">
                                <Card.Body>
                                    <Card.Title>Other Expenses</Card.Title>
                                    <Card.Text className="fs-4">
                                        ₹{request.OTHER_EXPENSE?.toFixed(2) || '0.00'}
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                        </Row>

                        {/* Additional Other Expenses */}
                        <Row>
                            <Col md={4}>
                                <Card className="mb-3">
                                    <Card.Body>
                                        <Card.Title>Printing & Stationery Charge</Card.Title>
                                        <Card.Text className="fs-4">
                                            ₹{request.STATIONARY?.toFixed(2) || '0.00'}
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="mb-3">
                                    <Card.Body>
                                        <Card.Title>Courier Charges</Card.Title>
                                        <Card.Text className="fs-4">
                                            ₹{request.COURIER?.toFixed(2) || '0.00'}
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                    {/* Grand Total */}
                    <Card className="bg-light">
                        <Card.Body className="text-end">
                            <h4 className="mb-0">
                                Total Amount: ₹{request.TOTAL_AMT?.toFixed(2) || '0.00'}
                            </h4>
                        </Card.Body>
                    </Card>
                </Card.Body>
            </Card>

            {/* Supporting Images */}
            <Card className="mb-4">
                <Card.Body>
                    <Card.Title>Supporting Documents</Card.Title>
                    {request.REQ_IMAGES?.length > 0 ? (
                        <div className="d-flex flex-wrap">
                            {request.REQ_IMAGES.map((img, index) => (
                                <div key={index} className="me-3 mb-3" style={{ width: '150px', cursor: 'pointer' }}>
                                    <Image
                                        src={img}
                                        thumbnail
                                        fluid
                                        onClick={() => {
                                            setSelectedImage(img);
                                            setShowImageModal(true);
                                        }}
                                    />
                                    <div className="text-center small mt-1">Document {index + 1}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <Alert variant="info">No supporting documents attached</Alert>
                    )}
                </Card.Body>
            </Card>

            {/* Notes */}
            <Card className="mb-4">
                <Card.Body>
                    <Card.Title>Employee Notes</Card.Title>
                    <Card.Text style={{ whiteSpace: 'pre-line' }}>
                        {request.NOTES || 'No additional notes provided'}
                    </Card.Text>
                </Card.Body>
            </Card>

            {/* Approval Section (only for pending requests) */}
            {((request.REQ_STATUS === 'Open' && hierarchy === 'Manager') || 
                (request.REQ_STATUS === 'Approved' && hierarchy === 'Admin')) && (
                <Card>
                    <Card.Body>
                                <Card.Title>Approval Action</Card.Title>
                                <table >
                                <th>Indicators: </th>
                                    <td className="d-flex flex-wrap gap-2">
                                        {getValidationTags(request).map((tag, i) => (
                                            <Badge key={i} bg={tag.pass ? "success" : "danger"}>
                                                {tag.label}
                                            </Badge>
                                        ))}
                                    </td>
                                </table>
                        <Form.Group className="mb-3">
                            <Form.Label>Comments</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={approvalComments}
                                onChange={(e) => setApprovalComments(e.target.value)}
                                placeholder="Enter your approval/rejection comments..."
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button
                                variant="danger"
                                onClick={() => handleApproveOrReject("Rejected")}
                                disabled={processing}
                            >
                                {processing ? <Spinner size="sm" /> : 'Reject Request'}
                            </Button>
                            <Button
                                variant="success"
                                onClick={() => handleApproveOrReject(hierarchy === "Admin" ? "Completed" : "Approved")}
                                disabled={processing}
                            >
                                {processing ? <Spinner size="sm" /> : hierarchy === "Admin" ? 'Complete Request' : 'Approve Request'}
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Approval History */}
            <Card>
                <Card.Body>
                    <Card.Title>Approval History</Card.Title>
                    <div className="timeline">
                        {request.APPROVER1_BY && (
                            <div className="timeline-item">
                                <div className="timeline-content">
                                    <strong>Manager Approval:</strong> {request.APPROVER1_BY}
                                        <div className="text-muted small mt-1">
                                                Updated on {formatDate(request.APPROVER1_ON)}
                                        </div>
                                        {request.APPROVER_COMMENT && (
                                            <div className="text-muted   mt-1">
                                                Comment: {request.APPROVER_COMMENT}
                                            </div>
                                        )}
                                </div>
                            </div>
                        )}
                        {request.APPROVER2_BY && (
                            <div className="timeline-item">
                                <div className="timeline-content">
                                    <strong>Admin Approval:</strong> {request.APPROVER2_BY}
                                    
                                        <div className="text-muted small mt-1">
                                                Updated on {formatDate(request.APPROVER2_ON)}
                                        </div>
                                        {request.PAYMENT_DETAILS && (
                                            <div className="text-muted   mt-1">
                                                Payment Details: {request.PAYMENT_DETAILS}
                                            </div>
                                        )}
                                </div>
                            </div>
                        )}
                        {request.AUDITOR_ON && (
                            <div className="timeline-item">
                                <div className="timeline-content">
                                    <strong>Audit Approval:</strong> {request.AUDITOR_BY}
                                        <div className="text-muted small mt-1">
                                                Updated on {formatDate(request.AUDITOR_ON)}
                                        </div>
                                </div>
                            </div>
                        )}
                        {!request.APPROVER1_BY && !request.APPROVER2_BY && !request.AUDITOR_ON && (
                            <div className="text-muted">No approval history yet</div>
                        )}
                    </div>
                </Card.Body>
            </Card>

            {/* Image Modal */}
            <Modal show={showImageModal} onHide={() => setShowImageModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Document View</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center p-0">
                    <Image src={selectedImage} fluid style={{ maxHeight: '70vh', width: 'auto' }} />
                </Modal.Body>
            </Modal>
        </div>
        </div>
        </div>
    );
};

export default ExpenseApprovalPage;